<script setup lang="ts">
import { useElementSize, useScroll } from '@vueuse/core';
import { computed, nextTick, onMounted, onUnmounted, ref, watch, type Ref } from 'vue';

const props = defineProps<{
  maxHeight?: string;
  elementRef?: Ref<HTMLElement | null>;
}>();

const internalElement = ref<HTMLElement>();
const element = computed(() => props.elementRef?.value ?? internalElement.value);

const { arrivedState, measure } = useScroll(element);
const { width, height } = useElementSize(element);

watch([width, height], measure);

const hasVerticalScrollbar = computed(() => {
  return !(arrivedState.top && arrivedState.bottom);
});

const hasHorizontalScrollbar = computed(() => {
  return !(arrivedState.left && arrivedState.right);
});

const scrollbarOffsetStyle = computed(() => ({
  '--vertical-scrollbar-offset': hasVerticalScrollbar.value ? 'var(--tml-scrollbar-width)' : '0px',
  '--horizontal-scrollbar-offset': hasHorizontalScrollbar.value ? 'var(--tml-scrollbar-height)' : '0px',
}));

const emit = defineEmits<{
  (e: 'scroll', event: Event): void;
  (e: 'scroll-vertical'): void;
}>();

function emitIfAtBottom() {
  measure();
  if (arrivedState.bottom) {
    emit('scroll-vertical');
  }
}

watch(
  () => arrivedState.bottom,
  (isAtBottom, wasAtBottom) => {
    if (isAtBottom && !wasAtBottom) {
      emit('scroll-vertical');
    }
  },
  { immediate: true },
);

let mutationObserver: MutationObserver | null = null;

onMounted(() => {
  if (!element.value) return;
  mutationObserver = new MutationObserver(() => {
    nextTick(emitIfAtBottom);
  });
  mutationObserver.observe(element.value, { childList: true, subtree: true });
});

onUnmounted(() => {
  mutationObserver?.disconnect();
  mutationObserver = null;
});
</script>

<template>
  <div class="tml-scroll-container relative overflow-hidden" :style="scrollbarOffsetStyle">
    <div
      :ref="props.elementRef ? undefined : (el) => (internalElement = el as HTMLElement)"
      :class="{ 'h-full overflow-auto': !props.elementRef }"
      :style="props.elementRef ? undefined : { maxHeight }"
      @scroll="!props.elementRef && emit('scroll', $event)"
    >
      <slot />
    </div>
    <Transition name="fade">
      <div
        v-if="!arrivedState.top"
        class="cover cover-top h-12 bg-gradient-to-b pointer-events-none"
      />
    </Transition>
    <Transition name="fade">
      <div
        v-if="!arrivedState.left"
        class="cover cover-left w-12 bg-gradient-to-r pointer-events-none"
      />
    </Transition>
    <Transition name="fade">
      <div
        v-if="!arrivedState.bottom"
        class="cover cover-bottom h-12 bg-gradient-to-t pointer-events-none"
      />
    </Transition>
    <Transition name="fade">
      <div
        v-if="!arrivedState.right"
        class="cover cover-right w-12 bg-gradient-to-l pointer-events-none"
      />
    </Transition>
  </div>
</template>

<style scoped>
.cover {
  @apply absolute from-[--tml-page-background-color];
}

.cover-top {
  top: 0;
  left: 0;
  right: 0;
}

.cover-left {
  top: 0;
  left: 0;
  bottom: 0;
}

.cover-bottom {
  bottom: 0;
  left: 0;
  right: 0;
}

.cover-right {
  top: 0;
  bottom: 0;
  right: 0;
}

/* Shift gradients to avoid obscuring scrollbars in Chrome/Safari */
@supports selector(::-webkit-scrollbar) {
  .cover-top {
    right: var(--vertical-scrollbar-offset);
  }

  .cover-left {
    bottom: var(--horizontal-scrollbar-offset);
  }

  .cover-bottom {
    bottom: var(--horizontal-scrollbar-offset);
    right: var(--vertical-scrollbar-offset);
  }

  .cover-right {
    right: var(--vertical-scrollbar-offset);
    bottom: var(--horizontal-scrollbar-offset);
  }
}
</style>
